// Pure helper for the collapsed tool-call row's summary label (AgentStreamView).
//
// Extracted out of the component so it's directly unit-testable (this package
// has no Svelte component-rendering test harness — see tool-call-file-path.ts
// for the same pattern). Returns a clean single label per ToolCallDetail type,
// falling back to the raw tool `name` only when the detail carries nothing more
// useful to show.

import type { ToolCallDetail } from "./types.js";

export interface ToolCallLabelInput {
  name: string;
  detail?: ToolCallDetail;
}

// ── The header-shape guard ───────────────────────────────────────────────────
//
// A collapsed tool row's label is a HEADER: one short line naming what the tool was
// asked to do. It is not a place for the tool's RESULT, and it is never a place for the
// provider framing that rides along with one.
//
// Measured on the session surface (a fan-out session, full page reload): a card's header
// line rendered, verbatim and visibly,
//
//   "[harness: subagent output matched instruction-shaped pattern(s): harness-envelope-tag.
//    Control tags below are neutralized …] I have a complete picture of the notification…"
//
// — a subagent's whole report, with the provider's internal envelope on the front. That
// string is NOT ours (nothing in this repo emits it): the agent SDK annotates a subagent's
// output with it and it reaches us inside a summary field. It cannot be removed at the
// source, so it is refused HERE, at the one place a label is chosen.
//
// The rule is SHAPE, not vocabulary: strip machine framing, then accept only what a header
// can actually be — a single line, no longer than a provider's own summary cap (160, the
// daemon's MAX_SUB_AGENT_SUMMARY_CHARS). Anything else is a body and is REFUSED outright,
// never truncated: the first N characters of a subagent's report IS the bug, not a smaller
// version of it. Callers fall back to the tool's own name, which leaks nothing.
// The guard itself lives in @cyborg7/observability/tool-words, shared with the daemon.
import { headerLabel } from "@cyborg7/observability/tool-words";
export { headerLabel };

// The provider tools that spawn a subagent: Claude's Task sidechain ("Task"/"Agent"/
// "Workflow", mirroring isClaudeSubagentToolName) and Codex's collab agent ("Sub-agent").
const SUBAGENT_TOOL_NAMES: ReadonlySet<string> = new Set([
  "Task",
  "Agent",
  "Workflow",
  "Sub-agent",
]);

// A quoted string field of a JS object literal, read out of source text. Quoted literals
// only: a template literal or a concatenated expression yields nothing and the caller falls
// back, which is the right answer — a half-evaluated expression is not a name.
function quotedField(source: string, field: string): string | null {
  const match = new RegExp(`\\b${field}\\s*:\\s*["']([^"'\\n]*)["']`).exec(source);
  return match?.[1] ?? null;
}

// How far past `export const meta` to look when the block has no `};` terminator — a
// malformed or minified script must not turn this into a scan of the whole file.
const WORKFLOW_META_SCAN_CHARS = 1000;

// What a WORKFLOW was launched to do, read out of the script it was handed.
//
// A workflow script is model-authored JS of a few kilobytes, and the DSL requires it to
// open with its own header block (packages/app/e2e/fixtures/claude-workflow/*.js):
//
//   export const meta = {
//     name: "paseo-project-inspection",
//     description: "Inspect Paseo and explain what the project does",
//     whenToUse: …, phases: […],
//   };
//
// That description is the one human sentence in the whole blob, and it is already this
// product's name for the same workflow everywhere else: the subagents track shows it,
// via the tool input's own `description` field (sidechain-tracker.ts). When the caller
// passes the script INLINE the field is absent and only the blob is left, which is how a
// fleet deploy ended up on the timeline as a card labelled nothing but "Workflow".
//
// Deliberately a scan of the header block, never an evaluation: the script is untrusted
// model-authored code, and a `description:` further down the file belongs to something
// else (a JSON schema property, for instance).
function workflowScriptLabel(script: string): string | null {
  const start = script.indexOf("export const meta");
  if (start < 0) return null;
  const blockEnd = script.indexOf("};", start);
  const block = script.slice(start, blockEnd > start ? blockEnd : start + WORKFLOW_META_SCAN_CHARS);
  return headerLabel(quotedField(block, "description")) ?? headerLabel(quotedField(block, "name"));
}

// What the subagent was ASKED to do, recovered from the Task tool's own INPUT.
//
// The typed `sub_agent` detail carries it as `description`, and that path is handled by the
// switch below. This covers the card that settled on the bare `unknown` envelope instead:
// the parent-side tool_result lands on the SAME callId as the accumulated sub_agent rows
// (see the clobber guard in state.svelte.ts), and when the `unknown` row is the only one a
// timeline kept, the task description survives ONLY in the raw input. Its OUTPUT never
// names the task, so it is not consulted.
//
// The last two candidates are how a WORKFLOW names itself when it carries no `description`
// of its own: the script it was pointed at, or — when the script came inline — the header
// block inside it. Both are gated by the same subagent-tool set as the rest of this
// function, so no other tool's input is mined for a field named `script`.
function subagentTaskLabel(entry: ToolCallLabelInput): string | null {
  if (!SUBAGENT_TOOL_NAMES.has(entry.name)) return null;
  const input = entry.detail?.type === "unknown" ? entry.detail.input : null;
  if (typeof input !== "object" || input === null) return null;
  const record = input as Record<string, unknown>;
  return (
    headerLabel(typeof record.description === "string" ? record.description : null) ??
    headerLabel(typeof record.subagent_type === "string" ? record.subagent_type : null) ??
    headerLabel(
      typeof record.scriptPath === "string" ? record.scriptPath.split(/[\\/]/).pop() : null,
    ) ??
    (typeof record.script === "string" ? workflowScriptLabel(record.script) : null)
  );
}

// What a one-line row's expansion adds beyond the line itself. A plain_text detail whose
// label and text only repeat the line (or are empty) adds nothing: the row gets no chevron
// and does not expand (Fab, 2026-10-07: "a dropdown with no meaning"). Others pass through.
const repeatsLine = (v: string | undefined, line: string) => !v?.trim() || v.trim() === line.trim();

export function detailBeyondLine(
  detail: ToolCallDetail | undefined,
  line: string,
): ToolCallDetail | undefined {
  if (detail?.type !== "plain_text") return detail;
  const repeats = (v?: string) => repeatsLine(v, line);
  const label = repeats(detail.label) ? undefined : detail.label;
  if (label === undefined && repeats(detail.text)) return undefined;
  return { ...detail, label };
}

// The same for a row's error: a failed Claude background task's error message IS its summary.
export function errorBeyondLine(error: unknown, line: string): unknown {
  if (!error) return undefined;
  let message: unknown = error;
  if (typeof error === "object" && "message" in error) message = error.message;
  return typeof message === "string" && message.trim() === line.trim() ? undefined : error;
}

// A plain_text detail's own summary: its label, or its text when there is no label. Both
// go through the shape guard — `text` in particular is the tool's OUTPUT body, which is
// exactly what a header must not become.
function plainTextLabel(detail: Extract<ToolCallDetail, { type: "plain_text" }>): string | null {
  return headerLabel(detail.label) ?? headerLabel(detail.text);
}

// A sub_agent detail names the TASK: what the subagent was asked to do, then what kind of
// subagent it was. Its result is not a candidate.
function subAgentLabel(detail: Extract<ToolCallDetail, { type: "sub_agent" }>): string | null {
  return headerLabel(detail.description) ?? headerLabel(detail.subAgentType);
}

// A `plain_text` detail is how synthetic tool calls (e.g. `task_notification`,
// see agent-sdk-types.ts) arrive — its `label`/`text` IS the human summary.
// Without a case for it here, callers fell through to `entry.name` (the raw,
// internal call name) and the collapsed row ended up printing that name TWICE
// side by side ("task_notification task_notification") since the row also
// shows `entry.name` next to this label.
export function toolCallLabel(entry: ToolCallLabelInput): string {
  if (!entry.detail) return entry.name;
  switch (entry.detail.type) {
    case "shell":
      return entry.detail.command.length > 60
        ? entry.detail.command.slice(0, 60) + "…"
        : entry.detail.command;
    case "read":
    case "edit":
    case "write":
      return entry.detail.filePath;
    case "search":
      return entry.detail.query;
    case "fetch":
      return entry.detail.url;
    case "plain_text":
      return plainTextLabel(entry.detail) ?? entry.name;
    case "sub_agent":
      return subAgentLabel(entry.detail) ?? entry.name;
    case "unknown":
      return subagentTaskLabel(entry) ?? entry.name;
    case "plan":
      return entry.detail.text.length > 60
        ? entry.detail.text.slice(0, 60) + "…"
        : entry.detail.text;
    case "worktree_setup":
      return entry.detail.branchName ?? entry.detail.worktreePath ?? entry.name;
    default:
      return entry.name;
  }
}

// MCP tools arrive with their raw internal call name `mcp__<server>__<tool>`
// (e.g. `mcp__cyborg7__cyborg7_read_docs`) — unreadable to a non-technical
// user. Humanize it to a plain label ("Read docs"). Native tools (Write, Read,
// Bash, ToolSearch, …) are already readable and pass through UNCHANGED. Never
// throws and never returns empty: a malformed `mcp__…` name is returned as-is.
export function toolDisplayName(name: string): string {
  if (!name.startsWith("mcp__")) return name;
  // `__` is the delimiter; server and tool may each carry single underscores,
  // so split on the delimiter and rejoin the tail as the tool name.
  const parts = name.split("__");
  if (parts.length < 3) return name; // malformed (e.g. `mcp__weird`) → as-is
  const server = parts[1];
  let tool = parts.slice(2).join("__");
  if (!server || !tool) return name;
  // Drop a redundant server prefix on the tool (`cyborg7_read_docs` → `read_docs`).
  if (tool.startsWith(server + "_")) tool = tool.slice(server.length + 1);
  const words = tool.replace(/_/g, " ").trim();
  if (!words) return name;
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

// ── Backgrounded launchers: the card must not claim a completion it cannot know ──
//
// Some tools do not do the work; they HAND IT OFF and return immediately. The tool call
// really is completed — and the work is not. The card only ever had "completed" to say,
// so it said it.
//
// MEASURED on the owner's daemon. A `Workflow` call's card flipped to completed at
// 16:11:50.181Z; the five child transcripts it launched opened at 16:11:50 — the same
// second, and they ran for minutes afterwards. Its own output was the giveaway:
//   "Workflow launched in background. Task ID: … You will be notified when it completes."
// Five minutes later the reader, looking at one green COMPLETED pill, asked why nothing
// had happened. The pill was true about the tool call and false about everything he cared
// about.
//
// Detected from SHAPE, never from a tool name, because Workflow is not the only launcher:
// `Bash` with run_in_background answers "Command running in background with ID: …", and
// our own create_agent / send_agent_prompt MCP tools take `background: true` and answer
// with a "you will get notified when …" guidance string (see paseo-tools.ts). All three
// deserve the same honesty, and a name list would have to be extended for each new one.
//
// Two structural signals, either is enough:
//   1. the INPUT asked for background explicitly, or
//   2. the RECEIPT the tool returned promises the work continues elsewhere.
const BACKGROUND_HANDOFF_RECEIPT =
  /\b(?:launched|running|started|continues|continuing)\s+in\s+(?:the\s+)?background\b|\byou\s+will\s+(?:be|get)\s+notified\b/i;

// A receipt is a promise, not a result: one or two lines. Anything longer is the tool's
// real output and is NOT scanned — otherwise a `Bash` that cats a file mentioning "running
// in the background", or a grep over this very file, would flip a truthful card.
const BACKGROUND_HANDOFF_RECEIPT_MAX_CHARS = 1000;

function declaresBackgroundInput(input: unknown): boolean {
  if (typeof input !== "object" || input === null) return false;
  const record = input as Record<string, unknown>;
  // `background` is our own MCP tools' flag; `run_in_background` is Claude's Bash.
  return record.background === true || record.run_in_background === true;
}

// The tool's output as scannable text. Serialized whole rather than picked apart field by
// field: the promise lands in a different key per tool (a bare string for Workflow, a
// nested `guidance` for our MCP tools), and the length cap above is what does the real
// discrimination anyway.
function backgroundHandoffReceipt(output: unknown): string | null {
  if (output === null || output === undefined) return null;
  let text: string;
  if (typeof output === "string") {
    text = output;
  } else {
    try {
      text = JSON.stringify(output);
    } catch {
      return null;
    }
  }
  return text.length > 0 && text.length <= BACKGROUND_HANDOFF_RECEIPT_MAX_CHARS ? text : null;
}

// Whether this tool call handed its work off to something that outlives the call.
// Detail-only (no status, no name) so the expanded card can ask the same question the
// collapsed header does.
//
// PAST TENSE, and the guard on `output` is what enforces it. A detail with no output has
// not returned — the same ToolCallDetail component renders PERMISSION requests
// (PermissionCard.svelte), which are shown BEFORE the tool runs and whose input can
// perfectly well carry `background: true`. Answering true there would tell the reader
// something was launched while they are still being asked whether to allow it. The cost is
// a launcher that returns nothing at all going unrecognised; every launcher we know of
// returns a receipt, and silence is not evidence of a handoff.
export function isBackgroundHandoffDetail(detail: ToolCallDetail | undefined): boolean {
  if (!detail) return false;
  // Only the two shapes a launcher's result actually lands in. A read/edit/write/search/
  // fetch detail carries FILE or USER content, which must never be pattern-matched for
  // product state — a launcher never lands there, so excluding them costs nothing.
  if (detail.type === "unknown") {
    if (detail.output === null || detail.output === undefined) return false;
    if (declaresBackgroundInput(detail.input)) return true;
    const receipt = backgroundHandoffReceipt(detail.output);
    return receipt !== null && BACKGROUND_HANDOFF_RECEIPT.test(receipt);
  }
  if (detail.type === "shell") {
    // The shell detail drops Bash's `run_in_background` flag on the way through
    // (toShellToolDetail keeps command/cwd/output/exitCode), so only the receipt is left.
    const receipt = backgroundHandoffReceipt(detail.output);
    return receipt !== null && BACKGROUND_HANDOFF_RECEIPT.test(receipt);
  }
  return false;
}

// The status word the CARD should show, which is not always the status the timeline
// recorded. A completed handoff reads "launched": the call is over, the work is not.
// Every other status passes through untouched — a launcher that is still running says
// running, and one that failed says failed.
export function toolCardStatus(entry: { status: string; detail?: ToolCallDetail }): string {
  if (entry.status === "completed" && isBackgroundHandoffDetail(entry.detail)) {
    return "launched";
  }
  return entry.status;
}

// Whether the collapsed row's summary label already stands on its own (a
// plain_text detail's label IS the human-readable summary) — showing the raw
// tool name alongside it would duplicate the same information in the row.
//
// Only when the summary REALLY stands on its own. A plain_text label that the header-shape
// guard REFUSES (a result body, a harness envelope) makes toolCallLabel fall back to the
// tool name — and the desktop row hides the name for plain_text, so the card would render
// with no text in it at all. Then the name is what carries the row.
export function shouldShowRawToolName(detail: ToolCallDetail | undefined): boolean {
  if (detail?.type !== "plain_text") return true;
  return plainTextLabel(detail) === null;
}
